import React from "react";
import {
    Box, Step, StepLabel, Stepper, TextField,
    Dialog, DialogTitle, DialogContent, DialogActions, Button,
    Autocomplete, CircularProgress, Typography,
} from "@mui/material";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import confetti from "../../../../../assets/audio/confetti.mp3";
import AppConfirmDialog from "components/Shared/AppConfirmDialog";
import { useVendorLeadDetailContext } from "contexts/VendorLeadContext";
import { useUpdateLeadStatusMutation } from "store/services/vendorLeadApi";
import { useAddVendorQuotationForPdfMailMutation, useLazyGetVendorQuotationsByLeadQuery } from "store/services/vendorQuotationApi";
import { useAddVendorQuotationExcelSendExcelMailMutation, useLazyGetVendorQuotationExcelsByLeadQuery } from "store/services/vendorQuotationExcelApi";
import LeadWonConfetti from "./Leadwonconfetti";


const LEAD_STEPS = [
    { label: "New", value: "New" },
    { label: "Contacted", value: "Contacted" },
    { label: "Quote Sent", value: "QuoteSent" },
    { label: "Negotiation", value: "Negotiation" },
    { label: "Won", value: "Won" },
    { label: "Lost", value: "Lost" },
];

const FROZEN_STATUSES = ["Won", "Lost"];
const ACTIVE_STEPS = ["New", "Contacted", "QuoteSent", "Negotiation"];

const isClickable = (step, currentStatus) => {
    if (FROZEN_STATUSES.includes(currentStatus)) return false;
    if (step.value === currentStatus) return false;
    const currentIndex = ACTIVE_STEPS.indexOf(currentStatus);
    const stepIndex = ACTIVE_STEPS.indexOf(step.value);
    if (step.value === "Lost") return true;
    if (step.value === "Won") return currentStatus === "Negotiation";
    return stepIndex === currentIndex + 1;
};

const requiresNote = (stepValue) => stepValue !== "New";
const isNoteValid = (text) => text.trim().length >= 3;
const isFinalStep = (stepValue) => stepValue === "Won" || stepValue === "Lost";

// ─── component ────────────────────────────────────────────────────────────────

const VendorLeadStepper = () => {
    const { id } = useParams();
    const { vendorLeadDetail } = useVendorLeadDetailContext();

    // ── stepper state ──
    const [pendingStep, setPendingStep] = React.useState(null);
    const [showConfetti, setShowConfetti] = React.useState(false);
    const [noteTouched, setNoteTouched] = React.useState(false);
    const [followUpDateTouched, setFollowUpDateTouched] = React.useState(false);
    const [noteContent, setNoteContent] = React.useState("");
    const [followUpDate, setFollowUpDate] = React.useState("");

    // ── lost dialog state ──
    const [lostModalOpen, setLostModalOpen] = React.useState(false);
    const [lostReason, setLostReason] = React.useState("");
    const [competitorInfo, setCompetitorInfo] = React.useState("");

    // ── QuoteSent: selected quotation / excel ──
    const [selectedQuotation, setSelectedQuotation] = React.useState(null);
    const [selectedExcel, setSelectedExcel] = React.useState(null);

    // ── API hooks ──
    const [updateLeadStatus, { isLoading: updatingStatus }] =
        useUpdateLeadStatusMutation();

    const [triggerGetQuotations, {
        data: quotationOptions = [],
        isFetching: fetchingQuotations,
    }] = useLazyGetVendorQuotationsByLeadQuery();

    const [triggerGetExcels, {
        data: excelOptions = [],
        isFetching: fetchingExcels,
    }] = useLazyGetVendorQuotationExcelsByLeadQuery();

    const [sendPdfMail] = useAddVendorQuotationForPdfMailMutation();
    const [sendExcelMail] = useAddVendorQuotationExcelSendExcelMailMutation();

    // ── fetch by lead ID when QuoteSent dialog opens ──
    React.useEffect(() => {
        if (pendingStep?.value !== "QuoteSent") return;
        triggerGetQuotations(id);
        triggerGetExcels(id);
    }, [pendingStep, id, triggerGetQuotations, triggerGetExcels]);

    // ── helpers ──────────────────────────────────────────────────────────────────

    const resetAll = () => {
        setPendingStep(null);
        setNoteContent("");
        setFollowUpDate("");
        setLostReason("");
        setCompetitorInfo("");
        setLostModalOpen(false);
        setNoteTouched(false);
        setFollowUpDateTouched(false);
        setSelectedQuotation(null);
        setSelectedExcel(null);
    };

    const handleStepClick = (step) => {
        if (!isClickable(step, currentStatus)) return;
        setNoteContent("");
        setFollowUpDate("");
        if (step.value === "Lost") {
            setLostReason("");
            setCompetitorInfo("");
            setLostModalOpen(true);
            setPendingStep(step);
            return;
        }
        setPendingStep(step);
    };

    // Called when user clicks Confirm in the main dialog
    const handleConfirm = async () => {
        const needsNote = requiresNote(pendingStep?.value);
        const needsFollowUp = !isFinalStep(pendingStep?.value);

        if (needsNote && !isNoteValid(noteContent)) {
            setNoteTouched(true);
            return;
        }
        if (needsFollowUp && !followUpDate) {
            setFollowUpDateTouched(true);
            return;
        }

        try {
            // 1. Update lead status first — if this fails we stop here
            await updateLeadStatus({
                id,
                body: {
                    status: pendingStep.value,
                    ...(noteContent.trim() && { noteContent: noteContent.trim() }),
                    ...(!isFinalStep(pendingStep.value) && followUpDate && {
                        nextFollowUpActionDate: followUpDate,
                    }),
                },
            }).unwrap();

            toast.success(`Lead status updated to ${pendingStep.label}`);

            // 2. Fire mail mutations in parallel (only if QuoteSent and something selected)
            if (pendingStep.value === "QuoteSent") {
                const mailCalls = [];

                if (selectedQuotation) {
                    mailCalls.push(
                        sendPdfMail({
                            vendorLeadId: id,
                            vendorQuotationId: selectedQuotation._id,
                            isQuoteSent: true,
                        }).unwrap()
                    );
                }
                if (selectedExcel) {
                    mailCalls.push(
                        sendExcelMail({
                            vendorLeadId: id,
                            vendorQuotationExcelId: selectedExcel._id,
                            isQuoteSent: true,
                        }).unwrap()
                    );
                }

                if (mailCalls.length > 0) {
                    const results = await Promise.allSettled(mailCalls);
                    results.forEach((r) => {
                        if (r.status === "rejected") {
                            toast.error(
                                r.reason?.data?.message ?? r.reason?.message ?? "Mail could not be sent"
                            );
                        } else {
                            toast.success("Quotation email sent successfully");
                        }
                    });
                }
            }

            if (pendingStep.value === "Won") {
                setShowConfetti(true);
                new Audio(confetti).play();
            }
        } catch (err) {
            toast.error(err?.data?.message ?? err?.message ?? "Could not update status");
        } finally {
            resetAll();
        }
    };

    // Called when user clicks Confirm in the Lost dialog
    const handleLostSubmit = async () => {
        try {
            await updateLeadStatus({
                id,
                body: {
                    status: "Lost",
                    ...(lostReason.trim() && { lostReason: lostReason.trim() }),
                    ...(competitorInfo.trim() && { competitorInfo: competitorInfo.trim() }),
                    ...(noteContent.trim() && { noteContent: noteContent.trim() }),
                },
            }).unwrap();
            toast.success("Lead marked as Lost");
        } catch (err) {
            toast.error(err?.data?.message ?? err?.message ?? "Could not update status");
        } finally {
            resetAll();
        }
    };

    const isConfirmDisabled = () => {
        if (!pendingStep) return true;
        if (requiresNote(pendingStep.value) && !isNoteValid(noteContent)) return true;
        if (!isFinalStep(pendingStep.value) && !followUpDate) return true;
        return false;
    };

    // ── derived values ───────────────────────────────────────────────────────────

    const currentStatus = vendorLeadDetail?.status;
    const activeStep = LEAD_STEPS.findIndex((s) => s.value === currentStatus);
    const isFrozen = FROZEN_STATUSES.includes(currentStatus);
    const todayStr = new Date().toISOString().split("T")[0];

    // ── shared field JSX ─────────────────────────────────────────────────────────

    const FollowUpDateField = (
        <TextField
            label="Follow-up Date"
            type="date"
            value={followUpDate}
            onChange={(e) => setFollowUpDate(e.target.value)}
            fullWidth
            required
            InputLabelProps={{ shrink: true }}
            inputProps={{ min: todayStr }}
            error={followUpDateTouched && !followUpDate}
            helperText={
                followUpDateTouched && !followUpDate ? "Follow-up date is required" : " "
            }
        />
    );

    const NoteField = (
        <TextField
            label="Note"
            value={noteContent}
            onChange={(e) => setNoteContent(e.target.value)}
            multiline
            rows={3}
            fullWidth
            required
            placeholder="Add a note about this status change… (min. 3 characters)"
            error={noteTouched && !isNoteValid(noteContent)}
            helperText={
                noteTouched && !isNoteValid(noteContent)
                    ? "Note must contain at least 3 characters"
                    : " "
            }
        />
    );

    // ── QuoteSent: optional attachment fields ────────────────────────────────────

    const QuoteSentFields = (
        <Box display="flex" flexDirection="column" gap={2}>
            <Typography variant="body2" color="text.secondary">
                Optionally attach a quotation or excel to send via email:
            </Typography>

            {/* Quotation PDF */}
            <Autocomplete
                options={quotationOptions}
                getOptionLabel={(o) => `${o.title ?? ""} - ${o.vendorQuotationId ?? ""}`}
                isOptionEqualToValue={(o, v) => o._id === v._id}
                loading={fetchingQuotations}
                value={selectedQuotation}
                onChange={(_, val) => setSelectedQuotation(val)}
                noOptionsText="No quotations found"
                renderInput={(params) => (
                    <TextField
                        {...params}
                        label="Attach Quotation (PDF)"
                        InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                                <>
                                    {fetchingQuotations && (
                                        <CircularProgress color="inherit" size={16} />
                                    )}
                                    {params.InputProps.endAdornment}
                                </>
                            ),
                        }}
                    />
                )}
            />

            {/* Quotation Excel */}
            <Autocomplete
                options={excelOptions}
                getOptionLabel={(o) => `${o.title ?? ""} - ${o.VendorQuotationExcelId ?? ""}`}
                isOptionEqualToValue={(o, v) => o._id === v._id}
                loading={fetchingExcels}
                value={selectedExcel}
                onChange={(_, val) => setSelectedExcel(val)}
                noOptionsText="No excel quotations found"
                renderInput={(params) => (
                    <TextField
                        {...params}
                        label="Attach Quotation (Excel)"
                        InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                                <>
                                    {fetchingExcels && (
                                        <CircularProgress color="inherit" size={16} />
                                    )}
                                    {params.InputProps.endAdornment}
                                </>
                            ),
                        }}
                    />
                )}
            />
        </Box>
    );

    // ── step styling ─────────────────────────────────────────────────────────────

    const getStepStyle = (index) => {
        const isActive = index === activeStep;
        const isCompleted = index < activeStep;
        if (isActive) {
            return {
                border: "2px solid", borderColor: "primary.main", borderRadius: 0,
                paddingX: 2, paddingY: 0.5,
                backgroundColor: "primary.main",
                "& .MuiStepLabel-label": { color: "white !important", fontWeight: "600 !important" },
            };
        }
        if (isCompleted) {
            return {
                border: "1px solid", borderColor: "primary.main", borderRadius: 0,
                paddingX: 2, paddingY: 0.5,
                backgroundColor: "primary.50",
                "& .MuiStepLabel-label": { color: "primary.main !important" },
            };
        }
        return {
            border: "1px solid", borderColor: "divider", borderRadius: 0,
            paddingX: 2, paddingY: 0.5,
            backgroundColor: "background.paper",
            "& .MuiStepLabel-label": { color: "text.secondary" },
        };
    };

    // ── render ───────────────────────────────────────────────────────────────────

    return (
        <>
            <LeadWonConfetti active={showConfetti} onDone={() => setShowConfetti(false)} />

            {/* Stepper */}
            <Stepper activeStep={activeStep}>
                {LEAD_STEPS.map((step, index) => {
                    const clickable = isClickable(step, currentStatus);
                    return (
                        <Step key={step.value}>
                            <StepLabel
                                onClick={() => handleStepClick(step)}
                                sx={{
                                    cursor: clickable ? "pointer" : "default",
                                    opacity: isFrozen && step.value !== currentStatus ? 0.4 : 1,
                                    "& .MuiStepLabel-iconContainer": { display: "none" },
                                    "& .MuiStepLabel-labelContainer": { ...getStepStyle(index) },
                                }}
                            >
                                {step.label}
                            </StepLabel>
                        </Step>
                    );
                })}
            </Stepper>

            {/* ── Confirm dialog (all non-Lost statuses) ── */}
            <AppConfirmDialog
                open={!!pendingStep && pendingStep?.value !== "Lost"}
                onClose={resetAll}
                onConfirm={handleConfirm}
                loading={updatingStatus}
                confirmDisabled={isConfirmDisabled()}
            >
                <Box display="flex" flexDirection="column" gap={2}>
                    <span>
                        {pendingStep?.value === "Won"
                            ? "A new Vendor and Vendor Profile will be created. Are you sure?"
                            : <>Change status to <strong>{pendingStep?.label}</strong>?</>
                        }
                    </span>

                    {!isFinalStep(pendingStep?.value) && FollowUpDateField}
                    {requiresNote(pendingStep?.value) && NoteField}
                    {pendingStep?.value === "QuoteSent" && QuoteSentFields}
                </Box>
            </AppConfirmDialog>

            {/* ── Lost dialog ── */}
            <Dialog open={lostModalOpen} onClose={resetAll} maxWidth="sm" fullWidth>
                <DialogTitle>Mark as Lost</DialogTitle>
                <DialogContent>
                    <Box display="flex" flexDirection="column" gap={3} mt={1}>
                        <TextField
                            label="Lost Reason"
                            value={lostReason}
                            onChange={(e) => setLostReason(e.target.value)}
                            multiline
                            rows={3}
                            fullWidth
                            placeholder="Why was this lead lost?"
                        />
                        <TextField
                            label="Competitor Info"
                            value={competitorInfo}
                            onChange={(e) => setCompetitorInfo(e.target.value)}
                            fullWidth
                            placeholder="Competitor name or details (optional)"
                        />
                        {NoteField}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={resetAll} disabled={updatingStatus}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        color="error"
                        onClick={handleLostSubmit}
                        disabled={updatingStatus || !lostReason.trim() || !isNoteValid(noteContent)}
                    >
                        {updatingStatus ? "Saving…" : "Mark as Lost"}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default VendorLeadStepper;